import React, { useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { useApps } from '@/contexts/AppsContext';
import { useAppId } from '@/hooks/useAppId';
import { useMindWebSocket } from '@/contexts/MindWebSocketContext';
import { mindApi } from '@/services/api';
import { Activity, BookOpen, Brain, Sparkles, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import PresenceTab from './PresenceTab';
import CuriosityTab from './CuriosityTab';
import DiaryTab from './DiaryTab';
import IntelligenceTab from './IntelligenceTab';

const TABS = [
  { id: 'presence', label: 'Presence', icon: Activity },
  { id: 'curiosity', label: 'Curiosity', icon: Sparkles },
  { id: 'diary', label: 'Diary', icon: BookOpen },
  { id: 'intelligence', label: 'Intelligence', icon: Brain },
];

export default function MindPage() {
  const appId = useAppId();
  const { currentApp, appReadiness } = useApps();
  const { connect, disconnect, subscribe, isConnected, isReconnecting, connectionState, WS_STATE } =
    useMindWebSocket();
  const [activeTab, setActiveTab] = useState('presence');
  const [presence, setPresence] = useState(null);
  const [presenceLoading, setPresenceLoading] = useState(true);
  const [presenceError, setPresenceError] = useState(null);
  const [realtimeThoughts, setRealtimeThoughts] = useState([]);

  // Fetch presence via REST (initial load + fallback)
  const fetchPresence = useCallback(async () => {
    if (!appId) return;
    setPresenceLoading(true);
    setPresenceError(null);

    try {
      const res = await mindApi.getPresence(appId);
      setPresence(res.data?.data || res.data);
    } catch (err) {
      setPresenceError(err.response?.data?.error || err.message);
    } finally {
      setPresenceLoading(false);
    }
  }, [appId]);

  // Connect WebSocket when Mind page mounts (pass tenantId for admin connections)
  // Backend maps tenantId → appId, so currentApp.appId IS the tenantId
  // connect() internally gets a fresh ticket via REST before opening the WebSocket.
  useEffect(() => {
    if (appId) {
      connect({ tenantId: appId });
    }
    return () => {
      disconnect();
    };
  }, [connect, disconnect, appId]);

  // Subscribe to real-time updates when connected
  useEffect(() => {
    if (!isConnected) return;

    // Presence updates — merge into existing state
    const unsubPresence = subscribe('presence_update', (message) => {
      setPresence((prev) => ({
        ...prev,
        heartbeat: message.data.heartbeat,
        attention: {
          ...prev?.attention,
          directives: message.data.directives?.length
            ? message.data.directives
            : prev?.attention?.directives,
          semantic_anchors: message.data.anchors?.length
            ? message.data.anchors
            : prev?.attention?.semantic_anchors,
        },
      }));
    });

    // New thoughts — prepend to real-time buffer
    const unsubThought = subscribe('new_thought', (message) => {
      setRealtimeThoughts((prev) => [message.data, ...prev]);
    });

    // Curiosity updates — toast notification for new packets
    const unsubCuriosity = subscribe('curiosity_update', (message) => {
      const count = message.data?.count || 0;
      if (count > 0) {
        toast.info(`${count} new curiosity packet${count !== 1 ? 's' : ''} generated`, {
          description: 'Switch to the Curiosity tab to review.',
          duration: 5000,
        });
      }
    });

    // System alerts — toast notification
    const unsubAlert = subscribe('system_alert', (message) => {
      const { status, alert_type } = message.data;
      if (status === 'CRITICAL') {
        toast.error(`System Alert: ${alert_type} — ${status}`, {
          description: 'Check the immune system dashboard for details.',
          duration: 10000,
        });
      } else if (status === 'DEGRADED') {
        toast.warning(`System Alert: ${alert_type} — ${status}`, {
          duration: 5000,
        });
      }
    });

    return () => {
      unsubPresence();
      unsubThought();
      unsubCuriosity();
      unsubAlert();
    };
  }, [isConnected, subscribe]);

  // Initial fetch — once per appId, not on every WS state change
  useEffect(() => {
    fetchPresence();
  }, [fetchPresence]);

  // Slow polling fallback when WebSocket is fully disconnected
  useEffect(() => {
    if (connectionState !== WS_STATE.DISCONNECTED || activeTab !== 'presence') return;
    const interval = setInterval(fetchPresence, 60000);
    return () => clearInterval(interval);
  }, [activeTab, connectionState, WS_STATE, fetchPresence]);

  if (!currentApp) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <div className="text-center space-y-2">
          <p className="text-sm">Select an app to view its mind.</p>
          <p className="text-xs">Use the app selector in the sidebar.</p>
        </div>
      </div>
    );
  }

  // Soft readiness guard — app needs setup before Mind can function
  const readiness = appReadiness[currentApp.appId];
  if (readiness && !readiness.loading && !readiness.hasApiKey) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <div className="text-center space-y-3">
          <p className="text-sm font-medium text-foreground">App setup incomplete</p>
          <p className="text-xs">Complete app setup before accessing Mind.</p>
          <a
            href={`/apps/${currentApp.appId}`}
            className="text-sm text-primary hover:underline"
          >
            Go to App Setup
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab navigation with connection indicator */}
      <div className="flex gap-1 border-b">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                isActive
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}

        {/* Connection status indicator */}
        <div className="ml-auto flex items-center gap-1.5 px-2 text-xs text-muted-foreground">
          {isConnected ? (
            <>
              <Wifi className="h-3 w-3 text-emerald-400" />
              <span className="text-emerald-400">Live</span>
            </>
          ) : isReconnecting ? (
            <>
              <Wifi className="h-3 w-3 text-amber-400 animate-pulse" />
              <span className="text-amber-400">Reconnecting</span>
            </>
          ) : (
            <>
              <WifiOff className="h-3 w-3" />
              <span>Polling</span>
            </>
          )}
        </div>
      </div>

      {/* Error banner */}
      {presenceError && activeTab === 'presence' && (
        <div className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-4 py-2">
          {presenceError}
        </div>
      )}

      {/* Tab content */}
      {activeTab === 'presence' && <PresenceTab presence={presence} loading={presenceLoading} />}
      {activeTab === 'curiosity' && <CuriosityTab appId={appId} />}
      {activeTab === 'diary' && <DiaryTab appId={appId} realtimeThoughts={realtimeThoughts} />}
      {activeTab === 'intelligence' && <IntelligenceTab appId={appId} />}
    </div>
  );
}
